<template>
   <div class="membership-print-page">
      <div class="print-toolbar">
         <div class="print-title">View Membership</div>
         <button type="button" class="print-button" @click="printPage">
            PRINT
         </button>
      </div>

      <div class="print-body">
         <div v-if="loading" class="loading">Loading membership information...</div>
         <div v-else-if="error" class="error">{{ error }}</div>
         <template v-else>
            <h2>[ {{ display(member.memberid) }} ] {{ display(member.fullname) }}</h2>

            <section class="print-section">
               <h3>General Membership Information</h3>
               <div class="general-grid">
                  <div class="field-list">
                     <div class="field-row" v-for="row in generalRows" :key="row.label">
                        <div class="field-label">{{ row.label }}</div>
                        <div class="field-value">{{ row.value }}</div>
                     </div>
                  </div>
                  <div class="logo-panel">
                     <img src="/images/miplogo-300x300.png" alt="MIP" />
                  </div>
               </div>
            </section>

            <section class="print-section">
               <h3>Personal Details</h3>
               <div class="field-row" v-for="row in personalRows" :key="row.label">
                  <div class="field-label">{{ row.label }}</div>
                  <div class="field-value">{{ row.value }}</div>
               </div>
            </section>

            <section class="print-section">
               <h3>Home Address</h3>
               <div class="field-row" v-for="row in homeRows" :key="row.label">
                  <div class="field-label">{{ row.label }}</div>
                  <div class="field-value">{{ row.value }}</div>
               </div>
            </section>

            <section class="print-section">
               <h3>Work Address</h3>
               <div class="field-row" v-for="row in workRows" :key="row.label">
                  <div class="field-label">{{ row.label }}</div>
                  <div class="field-value">{{ row.value }}</div>
               </div>
            </section>
         </template>
      </div>
   </div>
</template>

<script>
const axios = require('axios')

export default {
   name: 'MOAPrint',

   props: ['userid'],

   data() {
      return {
         loading: true,
         error: '',
         member: {}
      }
   },

   computed: {
      generalRows() {
         return [
            { label: 'Member ID', value: this.display(this.member.memberid) },
            { label: 'LPBM No.', value: this.display(this.member.lpbmno) },
            { label: 'Member Type', value: this.formatMemberType(this.member.membertype) },
            { label: 'Membership Status', value: this.formatMemberStatus(this.member.memberstatus) },
            { label: 'Date Applied', value: this.formatDate(this.member.dateapplied) },
            { label: 'Date Approved', value: this.formatDate(this.member.dateapproved) },
            { label: 'Date Payment (Registration)', value: this.formatDate(this.member.datepayment) },
         ]
      },

      personalRows() {
         return [
            { label: 'Full Name', value: this.display(this.member.fullname) },
            { label: 'IC / Passport No.', value: this.display(this.member.icpassport) },
            { label: 'Contact No.', value: this.display(this.member.contactmobile) },
            { label: 'Email', value: this.display(this.member.contactemail) },
            { label: 'Gender', value: this.display(this.member.gender) },
            { label: 'Nationality', value: this.display(this.member.nationality) },
            { label: 'Race', value: this.display(this.member.race) },
            { label: 'Religion', value: this.display(this.member.religion) },
            { label: 'Date of Birth', value: this.formatDate(this.member.dob) },
            { label: 'Age', value: this.ageFromDob(this.member.dob) },
            { label: 'Place of Birth', value: this.display(this.member.pob) },
         ]
      },

      homeRows() {
         return [
            { label: 'Address', value: this.display(this.member.homestreet) },
            { label: 'Postcode', value: this.display(this.member.homepostcode) },
            { label: 'City', value: this.display(this.member.homecity) },
            { label: 'District', value: this.display(this.member.homedistrict) },
            { label: 'State', value: this.display(this.member.homestate) },
            { label: 'Country', value: this.display(this.member.homecountry) },
            { label: 'Phone', value: this.display(this.member.homephone) },
         ]
      },

      workRows() {
         return [
            { label: 'Company', value: this.display(this.member.workcompany) },
            { label: 'Job Title', value: this.display(this.member.workjobtitle) },
            { label: 'Department', value: this.display(this.member.workdept) },
            { label: 'Position', value: this.display(this.member.workposition) },
            { label: 'Address', value: this.display(this.member.workstreet) },
            { label: 'Postcode', value: this.display(this.member.workpostcode) },
            { label: 'City', value: this.display(this.member.workcity) },
            { label: 'State', value: this.display(this.member.workstate) },
            { label: 'Country', value: this.display(this.member.workcountry) },
            { label: 'Phone', value: this.display(this.member.workphone) },
            { label: 'Fax', value: this.display(this.member.workfax) },
            { label: 'Email', value: this.display(this.member.workemail) },
         ]
      }
   },

   mounted() {
      this.loadMember()
   },

   methods: {
      loadMember() {
         axios.get('/member/info/' + this.userid)
            .then((resp) => {
               const rtn = resp.data || {}
               if (rtn.status === 'OK') {
                  this.member = rtn.data || {}
                  this.error = ''
               } else {
                  this.error = rtn.error || 'Unable to load membership information.'
               }
            })
            .catch((error) => {
               this.error = (error.response && error.response.data && error.response.data.message)
                  ? error.response.data.message
                  : 'Unable to load membership information.'
            })
            .finally(() => {
               this.loading = false
            })
      },

      printPage() {
         window.print()
      },

      display(value) {
         if (value === null || value === undefined || value === '') {
            return ''
         }
         return value
      },

      formatDate(value) {
         if (!value) {
            return ''
         }
         const date = new Date(value)
         if (Number.isNaN(date.getTime())) {
            return value
         }
         const day = String(date.getDate()).padStart(2, '0')
         const month = String(date.getMonth() + 1).padStart(2, '0')
         return `${day}/${month}/${date.getFullYear()}`
      },

      ageFromDob(value) {
         if (!value) {
            return ''
         }
         const dob = new Date(value)
         if (Number.isNaN(dob.getTime())) {
            return ''
         }
         const today = new Date()
         let age = today.getFullYear() - dob.getFullYear()
         const monthDiff = today.getMonth() - dob.getMonth()
         if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
            age -= 1
         }
         return age
      },

      formatMemberType(value) {
         const types = {
            ASC: 'Associate',
            STU: 'Student',
            GRA: 'Graduate',
            COR: 'Corporate',
            FEL: 'Fellow',
            HON: 'Honorary Fellow',
            PAT: 'Patron'
         }
         return types[value] || this.display(value)
      },

      formatMemberStatus(value) {
         const statuses = {
            0: 'Applying',
            1: 'Pending Approval',
            2: 'Pending Payment',
            3: 'Active - Good Standing',
            4: 'Suspended',
            5: 'Deceased'
         }
         return statuses[value] || this.display(value)
      }
   }
}
</script>

<style scoped>
.membership-print-page {
   background: #fff;
   color: #555;
   min-height: 100vh;
   font-family: Arial, Helvetica, sans-serif;
   font-size: 13px;
}

.print-toolbar {
   align-items: center;
   background: #252525;
   color: #fff;
   display: flex;
   height: 42px;
   justify-content: space-between;
   padding: 0 14px;
}

.print-title {
   font-size: 16px;
   font-weight: 600;
}

.print-button {
   background: #0d47a1;
   border: 0;
   border-radius: 3px;
   color: #fff;
   cursor: pointer;
   font-size: 12px;
   font-weight: 700;
   letter-spacing: .5px;
   padding: 8px 16px;
}

.print-body {
   padding: 16px 14px 40px;
}

h2 {
   color: #333;
   font-size: 17px;
   margin: 6px 0 18px;
}

.print-section {
   border-top: 1px solid #ddd;
   margin-top: 18px;
   padding-top: 16px;
}

.print-section h3 {
   color: #666;
   font-size: 13px;
   font-weight: 700;
   margin: 0 0 18px;
}

.general-grid {
   display: grid;
   grid-template-columns: minmax(0, 1fr) 360px;
   gap: 28px;
}

.logo-panel {
   align-items: center;
   display: flex;
   justify-content: center;
   min-height: 190px;
}

.logo-panel img {
   max-height: 150px;
   max-width: 190px;
}

.field-row {
   display: grid;
   grid-template-columns: 340px minmax(0, 1fr);
   min-height: 26px;
   padding: 2px 0;
}

.field-label {
   color: #666;
   font-weight: 700;
}

.field-value {
   color: #666;
}

.loading,
.error {
   padding: 18px 0;
}

.error {
   color: #b00020;
}

@media print {
   .print-button {
      display: none;
   }

   .print-toolbar {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
   }
}

@media (max-width: 800px) {
   .general-grid,
   .field-row {
      grid-template-columns: 1fr;
   }

   .logo-panel {
      justify-content: flex-start;
   }
}
</style>
